import { useCallback, useEffect, useRef, useState } from 'react'
import { Bell } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { useNativePush } from '@/features/notifications/hooks/use-native-push'
import { useWebPush } from '@/features/notifications/hooks/use-web-push'
import { enableWebPush } from '@/features/notifications/push/web-push'
import { enableNativePush } from '@/features/notifications/push/native-push'

const PROMPT_DISMISS_KEY = 'kuniri.push.prompt.dismissed.v1'
// Same pacing as the original activation dialog (home_screen.dart):
// 1.8 s after the shell settles.
const PROMPT_DELAY_MS = 1800
// "Non ora" and the backdrop behave like deferPushPrompt() upstream: the
// invitation comes back after one week, it is not a permanent opt-out.
const PROMPT_REMINDER_MS = 7 * 24 * 60 * 60_000

function dismissedRecently(): boolean {
  try {
    const raw = localStorage.getItem(PROMPT_DISMISS_KEY)
    if (!raw) return false
    const dismissedAt = Date.parse(raw)
    if (Number.isNaN(dismissedAt)) return false
    return Date.now() - dismissedAt < PROMPT_REMINDER_MS
  } catch {
    return false
  }
}

function deferPrompt(): void {
  try {
    localStorage.setItem(PROMPT_DISMISS_KEY, new Date().toISOString())
  } catch {
    // Storage being unavailable just means the prompt may reappear.
  }
}

const BODY_BY_STATE = {
  default: 'notifications.push.prompt.body',
  denied: 'notifications.push.prompt.bodyDenied',
  failed: 'notifications.push.prompt.bodyFailed',
  grantedFailed: 'notifications.push.prompt.bodyGranted',
} as const

/**
 * The same activation dialog the kuniri-original app used
 * (`_PushActivationDialog` in home_screen.dart, a Material AlertDialog):
 * centered modal with the bell icon, the "Vuoi restare aggiornato?" title, a
 * state-specific body and the action row "Non ora" / "Attiva notifiche"
 * ("Riprova" when permission is already granted). There is no floating close
 * icon; dismissing is the "Non ora" action, the Escape key or the backdrop.
 *
 * On the Android shell the dialog drives the *native* FCM path
 * (`enableNativePush`), so the invitation asks to enable the app
 * notifications the same way; in the browser it drives web push. The browser
 * permission itself is still requested only from the explicit button. This is
 * a once-per-week invitation: any dismissal defers it like upstream, and after
 * push is enabled it never comes back.
 */
export function PushPermissionPrompt() {
  const { t } = useTranslation()
  const nativePush = useNativePush()
  const webPush = useWebPush()
  const cardRef = useRef<HTMLDivElement | null>(null)
  const [deferred, setDeferred] = useState(dismissedRecently)
  const [ready, setReady] = useState(false)
  const [activating, setActivating] = useState(false)
  // A failed activation keeps the dialog open and swaps the body copy, like
  // the upstream dialog; the state is component-local because the activation
  // runs directly through the push modules.
  const [activateFailed, setActivateFailed] = useState(false)

  useEffect(() => {
    // Give the first screen a moment before proposing the permission.
    const timer = window.setTimeout(() => setReady(true), PROMPT_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [])

  const nativeSupported = nativePush.supported
  const push = nativeSupported ? nativePush : webPush
  const denied = push.permission === 'denied'
  // Upstream distinguishes "permission granted but the registration failed"
  // from "activation failed"; both keep the dialog open.
  const grantFailed = !denied && push.permission === 'granted' && activateFailed

  const bodyKey = denied
    ? nativeSupported
      ? 'notifications.nativePush.deniedBody'
      : BODY_BY_STATE.denied
    : grantFailed
      ? nativeSupported
        ? 'notifications.nativePush.enableError'
        : BODY_BY_STATE.grantedFailed
      : activateFailed
        ? nativeSupported
          ? 'notifications.nativePush.enableError'
          : BODY_BY_STATE.failed
        : nativeSupported
          ? 'notifications.nativePush.body'
          : BODY_BY_STATE.default

  const visible =
    ready &&
    !deferred &&
    push.supported &&
    push.permission !== 'unsupported' &&
    !push.subscribed &&
    // Web push additionally needs the VAPID key; the native path has none.
    (nativeSupported || webPush.keyState === 'available')

  const dismiss = useCallback(() => {
    deferPrompt()
    setDeferred(true)
  }, [])

  useEffect(() => {
    if (!visible) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        dismiss()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    cardRef.current?.focus()
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [visible, dismiss])

  async function enable() {
    if (activating) return
    setActivating(true)
    try {
      if (nativeSupported) {
        await enableNativePush()
      } else {
        await enableWebPush()
      }
      // Success closes like the original dialog (pop(true)); push is on now,
      // so the invitation must not come back this week.
      deferPrompt()
      setDeferred(true)
    } catch {
      // Upstream keeps the dialog open and swaps the body copy; the browser
      // permission state updates through the hooks.
      setActivateFailed(true)
    } finally {
      setActivating(false)
    }
  }

  if (!visible) return null

  const enableLabel = grantFailed
    ? nativeSupported
      ? t('notifications.nativePush.retry')
      : t('notifications.push.prompt.retry')
    : nativeSupported
      ? t('notifications.nativePush.enable')
      : t('notifications.push.prompt.enable')
  const enablingLabel = nativeSupported
    ? t('notifications.nativePush.enabling')
    : t('notifications.push.enabling')

  return (
    <div
      role="presentation"
      className="push-prompt"
      onClick={(event) => {
        // Barrier dismiss, same as barrierDismissible: true upstream.
        if (event.target === event.currentTarget) dismiss()
      }}
      onKeyDown={(event) => {
        // Barrier semantics; the Escape branch mirrors the window listener.
        if (event.key === 'Escape' && event.target === event.currentTarget) {
          dismiss()
        }
      }}
    >
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="push-prompt-title"
        aria-describedby="push-prompt-body"
        tabIndex={-1}
        className="push-prompt__card"
      >
        <Bell size={30} weight="regular" aria-hidden="true" />
        <h2 id="push-prompt-title" className="text-[19px] font-bold">
          {t('notifications.push.prompt.title')}
        </h2>
        <p id="push-prompt-body" className="text-[15px] text-muted">
          {t(bodyKey)}
        </p>
        <div className="push-prompt__actions">
          <button type="button" className="btn-ghost" onClick={dismiss}>
            {t('notifications.push.prompt.never')}
          </button>
          {!denied ? (
            <button
              type="button"
              className="btn-primary"
              disabled={activating}
              onClick={() => void enable()}
            >
              {activating ? enablingLabel : enableLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
